import { ReactFlowInstance, XYPosition } from "@xyflow/react";
import { NoxusReactflowNode } from "@/types/spotflow";
import { NodeDefinition } from "@/generated";

export const COMPACT_PICKER_CONNECTION_DOT_OFFSET = 13;
export const COMPACT_PICKER_CONNECTION_DOT_SIZE = 12;
export const COMPACT_PICKER_CONNECTION_CENTER_Y =
  COMPACT_PICKER_CONNECTION_DOT_OFFSET + COMPACT_PICKER_CONNECTION_DOT_SIZE / 2;
export const COMPACT_PICKER_WIDTH = 320;
export const COMPACT_PICKER_VIEWPORT_MARGIN = 10;
export const COMPACT_PICKER_HANDLE_OUTSET = 28;
// A node's connection handle reaches past its edge: the centre is
// COMPACT_PICKER_HANDLE_OUTSET (28) out and the handle is 18 wide, so its outer
// edge is another 9 beyond. Used to keep that handle clear of the config panel.
export const NODE_HANDLE_REACH = COMPACT_PICKER_HANDLE_OUTSET + 9;
export const COMPACT_PICKER_SEARCH_HEIGHT = 33;
// The V2 list holds this height whatever it lists, unless the canvas has less
// room, in which case it shrinks (down to the minimum) instead of panning.
export const COMPACT_PICKER_LIST_MAX_HEIGHT = 300;
// Five 40px rows: enough to scan without the picker feeling like a tooltip.
export const COMPACT_PICKER_LIST_MIN_HEIGHT = 200;
export const COMPACT_PICKER_ESTIMATED_MAX_HEIGHT =
  COMPACT_PICKER_SEARCH_HEIGHT + COMPACT_PICKER_LIST_MAX_HEIGHT;
export const COMPACT_PICKER_MIN_HEIGHT =
  COMPACT_PICKER_SEARCH_HEIGHT + COMPACT_PICKER_LIST_MIN_HEIGHT;
export const COMPACT_PICKER_NEW_NODE_GAP = 120;
export const CANVAS_PANE_TRANSITION_MS = 320;
export const DEFAULT_NODE_WIDTH = 300;
export const DEFAULT_NODE_HEIGHT = 100;
export const DEFAULT_NODE_HEADER_HEIGHT = 44;

function getNewNodeFlowPositionToRight(node: NoxusReactflowNode): XYPosition {
  const nodeWidth = node.measured?.width || node.width || DEFAULT_NODE_WIDTH;
  return {
    x: node.position.x + nodeWidth + COMPACT_PICKER_NEW_NODE_GAP,
    y: node.position.y,
  };
}

function getNewNodeFlowPositionToLeft(node: NoxusReactflowNode): XYPosition {
  return {
    x: node.position.x - DEFAULT_NODE_WIDTH - COMPACT_PICKER_NEW_NODE_GAP,
    y: node.position.y,
  };
}

export type CanvasScreenBounds = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export function getCanvasScreenBounds(
  pane: HTMLElement | null
): CanvasScreenBounds {
  const margin = COMPACT_PICKER_VIEWPORT_MARGIN;
  if (!pane) {
    return {
      left: margin,
      right: window.innerWidth - margin,
      top: margin,
      bottom: window.innerHeight - margin,
    };
  }

  const rect = pane.getBoundingClientRect();
  return {
    left: rect.left + margin,
    right: rect.right - margin,
    top: rect.top + margin,
    bottom: rect.bottom - margin,
  };
}

export function getFlowNodeBounds(node: NoxusReactflowNode): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  return {
    x: node.position.x,
    y: node.position.y,
    width: node.measured?.width ?? node.width ?? DEFAULT_NODE_WIDTH,
    height: node.measured?.height ?? node.height ?? DEFAULT_NODE_HEIGHT,
  };
}

export async function ensureCanvasRectInView(
  reactFlowInstance: ReactFlowInstance,
  rect: { x: number; y: number; width: number; height: number },
  pane: HTMLElement | null,
  options?: { padding?: number; duration?: number; bounds?: CanvasScreenBounds }
): Promise<void> {
  const padding = options?.padding ?? 48;
  const duration = options?.duration ?? 280;
  const bounds = options?.bounds ?? getCanvasScreenBounds(pane);

  const topLeft = reactFlowInstance.flowToScreenPosition({
    x: rect.x,
    y: rect.y,
  });
  const bottomRight = reactFlowInstance.flowToScreenPosition({
    x: rect.x + rect.width,
    y: rect.y + rect.height,
  });

  const minScreenX = Math.min(topLeft.x, bottomRight.x);
  const maxScreenX = Math.max(topLeft.x, bottomRight.x);
  const minScreenY = Math.min(topLeft.y, bottomRight.y);
  const maxScreenY = Math.max(topLeft.y, bottomRight.y);

  let shiftX = 0;
  let shiftY = 0;

  if (maxScreenX > bounds.right - padding) {
    shiftX -= maxScreenX - (bounds.right - padding);
  }
  if (minScreenX < bounds.left + padding) {
    shiftX += bounds.left + padding - minScreenX;
  }
  if (maxScreenY > bounds.bottom - padding) {
    shiftY -= maxScreenY - (bounds.bottom - padding);
  }
  if (minScreenY < bounds.top + padding) {
    shiftY += bounds.top + padding - minScreenY;
  }

  if (shiftX === 0 && shiftY === 0) {
    return;
  }

  const viewport = reactFlowInstance.getViewport();
  await reactFlowInstance.setViewport(
    {
      x: viewport.x + shiftX,
      y: viewport.y + shiftY,
      zoom: viewport.zoom,
    },
    { duration }
  );
}

// Clamps the picker box's left edge. `footprintWidth` is the width that must
// stay on-screen from the picker box's left — usually the node that replaces the
// picker (DEFAULT_NODE_WIDTH * zoom), which can be wider than the picker itself
// when zoomed in. Reserving it here means the placed node already fits, so no
// follow-up pan fires when it mounts. Source: node grows right from the box, so
// reserve `footprintWidth` on the right. Reversed: node grows left, so reserve
// the overflow on the left.
export function clampPickerScreenX(
  x: number,
  bounds: CanvasScreenBounds,
  options?: {
    handleOutset?: number;
    isReversed?: boolean;
    footprintWidth?: number;
  }
): number {
  const handleOutset = options?.handleOutset ?? 0;
  const isReversed = options?.isReversed ?? false;
  const footprintWidth = options?.footprintWidth ?? COMPACT_PICKER_WIDTH;
  const overflow = Math.max(0, footprintWidth - COMPACT_PICKER_WIDTH);
  const minX = bounds.left + (isReversed ? overflow : handleOutset);
  const maxX = isReversed
    ? bounds.right - COMPACT_PICKER_WIDTH - handleOutset
    : bounds.right - footprintWidth;
  if (x > maxX) x = maxX;
  if (x < minX) x = minX;
  return x;
}

export type CompactPickerVerticalPlacement = "below" | "above";

export interface CompactPickerFit {
  placement: CompactPickerVerticalPlacement;
  height: number;
}

// Vertical room the canvas offers the picker on one side of its anchor: from
// the box top down to the canvas bottom when growing down, from the canvas top
// up to the box bottom when growing up.
function compactPickerRoom(
  anchorScreenY: number,
  handleCenterY: number,
  placement: CompactPickerVerticalPlacement,
  bounds: CanvasScreenBounds
): number {
  return placement === "above"
    ? anchorScreenY + handleCenterY - bounds.top
    : bounds.bottom - (anchorScreenY - handleCenterY);
}

// The height the picker takes for a placement: its full height when the room
// allows, otherwise the room itself, never less than the minimum (which the
// opener then pans to fit).
export function compactPickerFitHeight(
  anchorScreenY: number,
  handleCenterY: number,
  placement: CompactPickerVerticalPlacement,
  bounds: CanvasScreenBounds
): number {
  const room = compactPickerRoom(
    anchorScreenY,
    handleCenterY,
    placement,
    bounds
  );
  return Math.min(
    COMPACT_PICKER_ESTIMATED_MAX_HEIGHT,
    Math.max(COMPACT_PICKER_MIN_HEIGHT, Math.floor(room))
  );
}

// The picker pins its connection handle to the anchor (a flow point, in screen
// space) and grows either down (handle at top) or up (handle at bottom). Prefer
// down at full height, then up at full height. When neither side has room for
// the whole picker (a short window with the anchor mid-canvas), shrink it onto
// the roomier side rather than panning the canvas; the opener pans only when
// even the minimum height does not fit. `anchorScreenY` is the handle-centre
// Y; `handleCenterY` is its offset from the picker's top.
export function resolveCompactPickerFit(
  anchorScreenY: number,
  handleCenterY: number,
  bounds: CanvasScreenBounds
): CompactPickerFit {
  const full = COMPACT_PICKER_ESTIMATED_MAX_HEIGHT;
  const belowTop = anchorScreenY - handleCenterY;
  if (belowTop >= bounds.top && belowTop + full <= bounds.bottom) {
    return { placement: "below", height: full };
  }
  const aboveBottom = anchorScreenY + handleCenterY;
  if (aboveBottom <= bounds.bottom && aboveBottom - full >= bounds.top) {
    return { placement: "above", height: full };
  }
  const roomBelow = compactPickerRoom(
    anchorScreenY,
    handleCenterY,
    "below",
    bounds
  );
  const roomAbove = compactPickerRoom(
    anchorScreenY,
    handleCenterY,
    "above",
    bounds
  );
  const placement: CompactPickerVerticalPlacement =
    roomAbove > roomBelow ? "above" : "below";
  return {
    placement,
    height: compactPickerFitHeight(
      anchorScreenY,
      handleCenterY,
      placement,
      bounds
    ),
  };
}

// The picker's top-left for a given placement, keeping the handle on the anchor.
// Below: top sits `handleCenterY` above the anchor. Above: the box bottom sits
// `handleCenterY` below the anchor, so the body grows upward.
export function compactPickerBoxTop(
  anchorScreenY: number,
  handleCenterY: number,
  pickerHeight: number,
  placement: CompactPickerVerticalPlacement
): number {
  return placement === "above"
    ? anchorScreenY + handleCenterY - pickerHeight
    : anchorScreenY - handleCenterY;
}

// Screen-space pan shift to bring a screen rect within the canvas bounds. The
// compact picker is a fixed overlay, so its fit is a screen-space question; the
// viewport translates by this shift (and so does the flow anchor it pins to).
export function computeScreenRectInViewShift(
  rect: { left: number; top: number; width: number; height: number },
  bounds: CanvasScreenBounds,
  padding = 0
): { x: number; y: number } {
  let x = 0;
  let y = 0;
  const right = rect.left + rect.width;
  const bottom = rect.top + rect.height;
  if (right > bounds.right - padding) x -= right - (bounds.right - padding);
  if (rect.left < bounds.left + padding) x += bounds.left + padding - rect.left;
  if (bottom > bounds.bottom - padding) y -= bottom - (bounds.bottom - padding);
  if (rect.top < bounds.top + padding) y += bounds.top + padding - rect.top;
  return { x, y };
}

// Deliberately unclamped: this is the picker's *anchor* at the drop point.
// The opener clamps a copy for the instant open, then pans the canvas so the
// anchor itself fits — clamping here would make the fit check always pass and
// suppress the pan.
export function compactPickerTopLeftFromConnectionPoint(
  connectionPoint: { x: number; y: number },
  handleType: "source" | "target",
  horizontalSpacing = 0,
  connectionCenterY = COMPACT_PICKER_CONNECTION_CENTER_Y
): { x: number; y: number } {
  return {
    x:
      handleType === "target"
        ? connectionPoint.x - COMPACT_PICKER_WIDTH - horizontalSpacing
        : connectionPoint.x + horizontalSpacing,
    y: connectionPoint.y - connectionCenterY,
  };
}

export const findCompatibleHandle = (
  nodeType: string,
  handleType: "source" | "target",
  sourceHandleId: string | null,
  nodesData: NodeDefinition[] | undefined
): string | null => {
  if (!nodesData) return null;

  const nodeDef = nodesData.find((n) => n.type === nodeType);
  if (!nodeDef) return null;

  const handles = handleType === "target" ? nodeDef.inputs : nodeDef.outputs;

  if (!handles || handles.length === 0) return null;

  const visibleHandles = (handles as any[]).filter(
    (h) => h.type !== "variable_type_input" && h.type !== "variable_type_output"
  );

  if (visibleHandles.length === 0) return null;

  const firstHandle = visibleHandles[0];

  const isSubHandleType =
    firstHandle.type === "variable_connector" ||
    firstHandle.type === "variable_type_size_connector";

  if (isSubHandleType) {
    const keyName = handleType === "target" ? "Input 1" : "Output 1";
    return `${firstHandle.name}::${keyName}`;
  }

  return firstHandle.name;
};

export const onDragStart = (
  event: React.DragEvent<HTMLButtonElement | HTMLDivElement>,
  nodeType: string,
  subflow_config?: {
    workflow_id: string;
    workflow_name: string;
    workflow_nodes: any[];
  }
) => {
  event.dataTransfer.setData(
    "application/reactflow",
    JSON.stringify({ nodeType, subflow_config })
  );
  event.dataTransfer.effectAllowed = "move";
};

export const getNodeAtPosition = (
  mousePosition: { x: number; y: number },
  reactFlowInstance: ReactFlowInstance
): NoxusReactflowNode | null => {
  const flowPosition = reactFlowInstance.screenToFlowPosition(mousePosition);
  const nodes = reactFlowInstance.getNodes() as NoxusReactflowNode[];

  for (const node of nodes) {
    const nodeWidth = node.measured?.width || node.width || DEFAULT_NODE_WIDTH;
    const nodeHeight =
      node.measured?.height || node.height || DEFAULT_NODE_HEIGHT;

    if (
      flowPosition.x >= node.position.x &&
      flowPosition.x <= node.position.x + nodeWidth &&
      flowPosition.y >= node.position.y &&
      flowPosition.y <= node.position.y + nodeHeight
    ) {
      return node;
    }
  }

  return null;
};

export const getFirstSourceHandle = (
  node: NoxusReactflowNode,
  edges?: { source: string; sourceHandle?: string | null }[]
): string | null => {
  const allHandles = getAllSourceHandles(node);

  if (allHandles.length === 0) {
    return null;
  }

  if (edges) {
    const freeHandles = getAllSourceHandles(node, edges, true);
    if (freeHandles.length > 0) {
      return freeHandles[0];
    }
  }

  return allHandles[0];
};

export const getFirstTargetHandle = (
  node: NoxusReactflowNode,
  edges?: { target: string; targetHandle?: string | null }[]
): string | null => {
  const allHandles = getAllTargetHandles(node);

  if (allHandles.length === 0) {
    return null;
  }

  if (edges) {
    const freeHandles = getAllTargetHandles(node, edges, true);
    if (freeHandles.length > 0) {
      return freeHandles[0];
    }
  }

  return allHandles[0];
};

export const getAllSourceHandles = (
  node: NoxusReactflowNode,
  edges?: { source: string; sourceHandle?: string | null }[],
  onlyFree: boolean = false
): string[] => {
  const outputs = node.data.config.outputs || [];

  const visibleOutputs = outputs.filter((output: any) => {
    if (
      output.type === "variable_type_input" ||
      output.type === "variable_type_output"
    ) {
      return false;
    }
    if (
      output.type === "variable_connector" ||
      output.type === "variable_type_size_connector" ||
      output.type === "variable_type_connector" ||
      output.name === "inputs" ||
      output.name === "outputs"
    ) {
      return true;
    }
    return output.visible !== false;
  });

  const handles: string[] = [];

  const isSubHandleType = (type: string) =>
    type === "variable_connector" || type === "variable_type_size_connector";

  for (const output of visibleOutputs) {
    if (isSubHandleType(output.type) && output.keys) {
      for (const key of output.keys) {
        handles.push(`${output.name}::${key}`);
      }
    } else {
      handles.push(output.name);
    }
  }

  if (onlyFree && edges) {
    return handles.filter((handle) => {
      return !edges.some(
        (edge) => edge.source === node.id && edge.sourceHandle === handle
      );
    });
  }

  return handles;
};

export const getAllTargetHandles = (
  node: NoxusReactflowNode,
  edges?: { target: string; targetHandle?: string | null }[],
  onlyFree: boolean = false
): string[] => {
  const inputs = node.data.config.inputs || [];

  const visibleInputs = inputs.filter((input: any) => {
    if (
      input.type === "variable_type_input" ||
      input.type === "variable_type_output"
    ) {
      return false;
    }
    if (
      input.type === "variable_connector" ||
      input.type === "variable_type_size_connector" ||
      input.type === "variable_type_connector" ||
      input.name === "inputs" ||
      input.name === "outputs"
    ) {
      return true;
    }
    return input.visible !== false;
  });

  const handles: string[] = [];

  const isSubHandleType = (type: string) =>
    type === "variable_connector" || type === "variable_type_size_connector";

  for (const input of visibleInputs) {
    if (isSubHandleType(input.type) && input.keys) {
      for (const key of input.keys) {
        handles.push(`${input.name}::${key}`);
      }
    } else {
      handles.push(input.name);
    }
  }

  if (onlyFree && edges) {
    return handles.filter((handle) => {
      return !edges.some(
        (edge) => edge.target === node.id && edge.targetHandle === handle
      );
    });
  }

  return handles;
};

export const hasOnlyInputs = (node: NoxusReactflowNode): boolean => {
  const outputs = node.data.config.outputs || [];
  const inputs = node.data.config.inputs || [];

  const nonMagicalOutputs = outputs.filter(
    (output: any) =>
      output.type !== "variable_type_input" &&
      output.type !== "variable_type_output"
  );

  const nonMagicalInputs = inputs.filter(
    (input: any) =>
      input.type !== "variable_type_input" &&
      input.type !== "variable_type_output"
  );

  return nonMagicalOutputs.length === 0 && nonMagicalInputs.length > 0;
};

export const getRightEdgePosition = (
  node: NoxusReactflowNode,
  reactFlowInstance: ReactFlowInstance
): { x: number; y: number } => {
  return reactFlowInstance.flowToScreenPosition(
    getNewNodeFlowPositionToRight(node)
  );
};

export const getNodePositionToRight = (
  node: NoxusReactflowNode,
  reactFlowInstance: ReactFlowInstance
): { x: number; y: number } => {
  return reactFlowInstance.flowToScreenPosition(
    getNewNodeFlowPositionToRight(node)
  );
};

export const getLeftEdgePosition = (
  node: NoxusReactflowNode,
  reactFlowInstance: ReactFlowInstance
): { x: number; y: number } => {
  return reactFlowInstance.flowToScreenPosition(
    getNewNodeFlowPositionToLeft(node)
  );
};

export const getNodePositionToLeft = (
  node: NoxusReactflowNode,
  reactFlowInstance: ReactFlowInstance
): { x: number; y: number } => {
  return reactFlowInstance.flowToScreenPosition(
    getNewNodeFlowPositionToLeft(node)
  );
};

export const selectNode = (
  nodeId: string,
  nodes: NoxusReactflowNode[]
): NoxusReactflowNode[] => {
  return nodes.map((node) => ({
    ...node,
    selected: node.id === nodeId,
  }));
};

export const ensurePositionInView = async (
  targetPosition: XYPosition,
  reactFlowInstance: ReactFlowInstance,
  padding: number = 100,
  drawerWidth: number = 0
): Promise<void> => {
  const viewport = reactFlowInstance.getViewport();
  const { zoom } = viewport;

  const viewportWidth = window.innerWidth - drawerWidth;
  const viewportHeight = window.innerHeight;

  const screenPos = reactFlowInstance.flowToScreenPosition(targetPosition);

  const needsPan =
    screenPos.x < padding ||
    screenPos.x > viewportWidth - padding ||
    screenPos.y < padding ||
    screenPos.y > viewportHeight - padding;

  if (needsPan) {
    const availableWidth = window.innerWidth - drawerWidth;
    const availableCenterX = availableWidth / (2 * zoom);
    const viewportCenterX = window.innerWidth / (2 * zoom);
    const panOffsetX = drawerWidth > 0 ? availableCenterX - viewportCenterX : 0;

    await reactFlowInstance.setCenter(
      targetPosition.x - panOffsetX,
      targetPosition.y,
      {
        zoom,
        duration: 300,
      }
    );
  }
};

export const DRAWER_WIDTH = 560;

export const centerNodeInViewWithDrawer = async (
  node: NoxusReactflowNode,
  reactFlowInstance: ReactFlowInstance,
  duration: number = 300
): Promise<void> => {
  const viewport = reactFlowInstance.getViewport();
  const { zoom } = viewport;

  const viewportWidth = window.innerWidth;

  const nodeWidth = node.measured?.width || node.width || DEFAULT_NODE_WIDTH;
  const nodeHeight = node.measured?.height || node.height || 100;

  const nodeLeftScreen = reactFlowInstance.flowToScreenPosition({
    x: node.position.x,
    y: node.position.y,
  });

  const nodeRightScreen = reactFlowInstance.flowToScreenPosition({
    x: node.position.x + nodeWidth,
    y: node.position.y,
  });

  const drawerLeftEdge = viewportWidth - DRAWER_WIDTH;

  const isNodeCoveredByDrawer = nodeRightScreen.x > drawerLeftEdge;

  if (!isNodeCoveredByDrawer) {
    return;
  }

  const availableWidth = viewportWidth - DRAWER_WIDTH;

  const nodeCenterX = node.position.x + nodeWidth / 2;
  const nodeCenterY = node.position.y + nodeHeight / 2;

  const viewportCenterX = viewportWidth / (2 * zoom);

  const availableCenterX = availableWidth / (2 * zoom);

  const panOffsetX = availableCenterX - viewportCenterX;

  await reactFlowInstance.setCenter(nodeCenterX - panOffsetX, nodeCenterY, {
    zoom,
    duration,
  });
};
